/**
 * B-24 — real Today-hero weather (founder decision 2a).
 *
 * Both real-data `renderLoopHero` call sites passed the `ramiroWeather`
 * FIXTURE, so every athlete saw "57° overcast". This hook fetches real
 * current conditions via the SAME provider path as the native E1 run rider
 * (`lib/run/weather.ts` — reused, not duplicated; post task-39 that is our
 * `/api/weather` behind `weather_apple_v1`, and flag-off means no weather
 * row), from the athlete's stored location (`athlete_context._latitude` /
 * `._longitude`, written by the Settings Location modal).
 *
 * Contract (all fail-silent, hero-first):
 *   - No stored location → no fetch → `data` stays null → the hero meta
 *     weather row is simply ABSENT (composeHeroMeta drops empty items).
 *   - Any failure — offline, timeout, non-200, bad shape — resolves null
 *     inside `fetchRunWeather`; same absent row. Never throws, never
 *     surfaces an error state.
 *   - NEVER delays the hero: the hero renders immediately with whatever
 *     `data` is (null at first); the weather row appears when resolved.
 *   - °F + mph imperial, matching run-spike's hardcoded stats.
 *
 * Demo mode (`?user=ramiro`) never mounts this hook's caller with a real
 * season — the fixture stays the demo's weather (pinned in
 * heroWeather.b24.test.ts).
 */

import { useEffect, useState } from 'react';
import { fetchRunWeather, type RunWeather } from '../lib/run/weather';
import type { IconName } from '../components/icons/registry';

export interface HeroWeather {
  /** "57°" — whole degrees Fahrenheit with the ° suffix. */
  tempDisplay: string;
  /** "overcast" — short WMO-derived word; absent when the code was unknown. */
  condition?: string;
  windMph: number;
}

/** Pure projection: run-rider weather → the hero meta shape. Exported for
 *  the test file; not part of the public hook API. */
export function heroWeatherFromRun(
  w: Pick<RunWeather, 'tempF' | 'windMph' | 'condition'>,
): HeroWeather {
  return {
    tempDisplay: `${w.tempF}°`,
    windMph: w.windMph,
    ...(w.condition ? { condition: w.condition } : {}),
  };
}

/**
 * CD visual polish §1 — condition word → ratified `wx.*` glyph.
 *
 * The word is `HeroWeather.condition`, the short lowercase WMO-derived string
 * `api/lib/apple-data.js#CONDITION_WORD` produces ("overcast", "partly
 * cloudy"). The map is EXACTLY the ruled one and not a word wider: the design
 * ruling lists these fifteen and says an unknown or absent word renders NO
 * glyph, never a placeholder. Words the server can emit but the ruling did not
 * cover ("flurries", "windy", "hail", "hot") therefore fall
 * through to null by design ("heavy rain" and "heavy snow" joined the map by
 * founder ruling 2026-08-31) — the row renders exactly as it does today, and
 * widening this map is a design ruling, not an implementation detail.
 *
 * `wx.partly` is `sys.weather` verbatim (icons.json `aliasOf`), so partly
 * cloudy costs no new art.
 */
export const WEATHER_CONDITION_GLYPH: Readonly<Record<string, IconName>> = {
  clear: 'wx.clear',
  sunny: 'wx.clear',
  'mostly clear': 'wx.clear',
  'partly cloudy': 'wx.partly',
  cloudy: 'wx.overcast',
  overcast: 'wx.overcast',
  fog: 'wx.fog',
  mist: 'wx.fog',
  haze: 'wx.fog',
  drizzle: 'wx.rain',
  rain: 'wx.rain',
  showers: 'wx.rain',
  'heavy rain': 'wx.rain',
  snow: 'wx.snow',
  sleet: 'wx.snow',
  'heavy snow': 'wx.snow',
  thunderstorm: 'wx.storm',
};

/**
 * The condition glyph for a weather word, or null when there is nothing ruled
 * to draw. Pure — the demo fixture (`?user=ramiro`) resolves through this same
 * call for its fixed word, so the demo hero and a real one cannot diverge.
 */
export function weatherGlyphFor(condition?: string | null): IconName | null {
  if (!condition) return null;
  return WEATHER_CONDITION_GLYPH[condition.trim().toLowerCase()] ?? null;
}

export interface UseHeroWeatherResult {
  /** Null until (and unless) a fetch resolves — the caller renders no
   *  weather row for null. */
  data: HeroWeather | null;
  /**
   * W1-TODAY-FIX1 (F3) — true while a fetch for the CURRENT coordinates is in flight
   * (they are stored, the hook is enabled, and the fetch has not yet resolved or
   * failed). Derived at render, so it is already true on the very render the
   * coordinates arrive: the 2.0 cold-load gate holds the page for it, so the weather
   * row lands with the first paint instead of pushing the week card down after it.
   * Additive: nothing about `data` or when it resolves changes, and the legacy hero
   * does not read it.
   */
  loading: boolean;
}

export function useHeroWeather(args: {
  enabled: boolean;
  /** Stored `_latitude` when it is a finite number; null otherwise. */
  latitude: number | null;
  /** Stored `_longitude` when it is a finite number; null otherwise. */
  longitude: number | null;
}): UseHeroWeatherResult {
  const { enabled, latitude, longitude } = args;
  const [data, setData] = useState<HeroWeather | null>(null);
  // The coordinates whose fetch has finished (resolved, null or thrown). `loading`
  // compares it with the coordinates now wanted, at render, so there is no frame in
  // which a fetch is wanted and `loading` still reads false.
  const [doneFor, setDoneFor] = useState<string | null>(null);
  const wantedKey =
    enabled && latitude !== null && longitude !== null ? `${latitude},${longitude}` : null;

  useEffect(() => {
    if (!enabled || latitude === null || longitude === null) return;
    let cancelled = false;
    const key = `${latitude},${longitude}`;
    void fetchRunWeather(latitude, longitude)
      .then((w) => {
        // Null result (any failure) → leave data null; the row stays absent.
        if (!cancelled && w) setData(heroWeatherFromRun(w));
      })
      .catch(() => {
        // fetchRunWeather never throws (fail-silent); this only keeps `loading` honest.
      })
      .finally(() => {
        if (!cancelled) setDoneFor(key);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, latitude, longitude]);

  return { data, loading: wantedKey !== null && doneFor !== wantedKey };
}
